import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { toast } from 'sonner'
import {
  createDummyBotThunk,
  deleteDummyBotThunk,
  fetchDummyBotsThunk,
} from '@/redux/asyncThunks/dummyBot'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import {
  selectDummyBots,
  selectDummyBotsLoading,
} from '@/redux/slices/dummyBotSlice'
import { LLMChoice } from '@/redux/types/room'
import AIPlayerConfigForm from '@/components/settings/AIPlayerConfigForm'
import { fetchMCPServers } from '@/api/mcpServer'
import {
  listRemoteMCPServers,
  fetchRoomOwnerMCPConnections,
  AnyMCPServer,
} from '@/api/remoteMCP'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import ConfirmationModal from '@/components/OGE/OGEDeletionModal'
import { ImportModal } from '@/components/Room/ImportModal'
import { Trash2, FileDown } from 'lucide-react'
import { getAccessToken } from '@/utils/authUtils'
import {
  connectAIPlayerTestChatWebSocket,
  disconnectAIPlayerTestChatWebSocket,
} from '@/redux/asyncThunks/aiPlayerTestChatWebsocket'

export default function AIPlayerSettings() {
  const dispatch = useAppDispatch()
  const { roomId } = useParams<{ roomId: string }>()
  const jwtToken = getAccessToken()
  const chatBoxesOpen = useAppSelector(
    (s) => s.aiPlayerTestChatMessages.openChatBoxes.length > 0
  )
  const bots = useAppSelector(selectDummyBots)
  const botsLoading = useAppSelector(selectDummyBotsLoading)
  const [servers, setServers] = useState<AnyMCPServer[]>([])
  const [ownerConnectedIds, setOwnerConnectedIds] = useState<Set<number>>(
    new Set()
  )
  const [selectedBotId, setSelectedBotId] = useState<string>('')
  const [showCreate, setShowCreate] = useState<boolean>(false)
  const [showDelete, setShowDelete] = useState<boolean>(false)
  const [botName, setBotName] = useState<string>('')
  const [botPersona, setBotPersona] = useState<string>('')
  const [creating, setCreating] = useState<boolean>(false)
  const [importAIPlayersOpen, setImportAIPlayersOpen] = useState<boolean>(false)

  const selectedBot = useMemo(
    () => bots.find((b) => String(b.id) === selectedBotId),
    [bots, selectedBotId]
  )

  const loadBots = () => {
    if (!roomId) return
    dispatch(fetchDummyBotsThunk(roomId))
  }

  useEffect(() => {
    if (!selectedBotId && bots.length > 0) {
      setSelectedBotId(String(bots[0].id))
    }
    if (selectedBotId && !bots.some((b) => String(b.id) === selectedBotId)) {
      setSelectedBotId(bots.length > 0 ? String(bots[0].id) : '')
    }
  }, [bots])

  useEffect(() => {
    if (!roomId) return
    loadBots()
    Promise.all([
      fetchMCPServers(roomId),
      listRemoteMCPServers(),
      fetchRoomOwnerMCPConnections(roomId).catch(() => ({
        connected_server_ids: [] as number[],
      })),
    ])
      .then(([roomServers, remoteServers, ownerConns]) => {
        const merged: AnyMCPServer[] = [...roomServers]
        remoteServers.forEach((remoteServer) => {
          if (!merged.find((existingServer) => existingServer.id === remoteServer.id))
            merged.push(remoteServer)
        })
        setServers(merged)
        setOwnerConnectedIds(new Set(ownerConns.connected_server_ids))
      })
      .catch(() => toast.error('Failed to load MCP servers'))
  }, [roomId])

  useEffect(() => {
    if (jwtToken && roomId && selectedBotId) {
      dispatch(
        connectAIPlayerTestChatWebSocket({
          jwtToken,
          botId: selectedBotId,
          roomId,
        })
      )
    }
    return () => {
      dispatch(disconnectAIPlayerTestChatWebSocket())
    }
  }, [dispatch, jwtToken, roomId, selectedBotId])

  const createBot = async () => {
    if (!roomId) return
    if (!botName.trim() || !botPersona.trim()) {
      toast.error('Name and persona instructions are required')
      return
    }
    setCreating(true)
    try {
      const created = await dispatch(
        createDummyBotThunk({
          roomId,
          payload: {
            name: botName.trim(),
            persona_instructions: botPersona.trim(),
            chatbot_llm: LLMChoice.CLAUDE,
            is_active: true,
            temperature: 0.7,
            max_tokens: 1024,
            history_limit: 25,
            rag_enabled: true,
            rag_top_k: 5,
            rag_chunk_size: 1000,
            rag_chunk_overlap: 200,
            rag_similarity_threshold: 0.2,
            rag_max_context_length: 3000,
          },
        })
      ).unwrap()
      toast.success('AI player created')
      setShowCreate(false)
      setBotName('')
      setBotPersona('')
      await loadBots()
      setSelectedBotId(String(created.id))
    } catch (error: any) {
      toast.error(error || 'Failed to create AI player')
    } finally {
      setCreating(false)
    }
  }

  const deleteBot = async () => {
    if (!selectedBot) return
    try {
      await dispatch(deleteDummyBotThunk(selectedBot.id)).unwrap()
      toast.success('AI player deleted')
      setShowDelete(false)
      await loadBots()
    } catch (error: any) {
      toast.error(error || 'Failed to delete AI player')
    }
  }

  return (
    <div className='px-24 py-6 space-y-6'>
      <h1 className='text-2xl font-bold text-dark-blue'>AI Player Settings</h1>
      <p className='text-muted-foreground'>
        Configure dummy AI players with persona, model, RAG documents, and MCP tools.
      </p>

      <div className='flex flex-wrap items-center justify-between gap-2'>
        <Button
          className='bg-dark-blue hover:bg-dark-blue-hovered'
          onClick={() => setShowCreate(true)}
          disabled={chatBoxesOpen}
        >
          Create AI Player
        </Button>
        {roomId && (
          <Button
            type='button'
            variant='outline'
            className='mr-8 border-dark-blue text-dark-blue hover:bg-slate-50'
            onClick={() => setImportAIPlayersOpen(true)}
            disabled={chatBoxesOpen}
          >
            <FileDown className='w-4 h-4 mr-2' />
            Import AI Players
          </Button>
        )}
      </div>

      <div className='space-y-2'>
        <Label>Select AI Player</Label>
        <div className='flex gap-2 items-center'>
          {botsLoading && bots.length === 0 ? (
            <div className='h-10 w-[300px] animate-pulse rounded-md bg-slate-200' />
          ) : (
            <Select
              value={selectedBotId}
              onValueChange={setSelectedBotId}
              disabled={chatBoxesOpen}
            >
              <SelectTrigger className='w-[300px]'>
                <SelectValue placeholder='Select AI player' />
              </SelectTrigger>
              <SelectContent>
                {bots.map((bot) => (
                  <SelectItem key={bot.id} value={String(bot.id)}>
                    {bot.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Button
            type='button'
            variant='outline'
            className='text-red-600 border-red-300 hover:bg-red-50'
            onClick={() => setShowDelete(true)}
            disabled={!selectedBot || chatBoxesOpen}
          >
            <Trash2 className='w-4 h-4' />
          </Button>
        </div>
      </div>

      {selectedBot && roomId ? (
        <AIPlayerConfigForm
          bot={selectedBot}
          onSaved={loadBots}
          servers={servers}
          ownerConnectedIds={ownerConnectedIds}
        />
      ) : (
        <div className='text-sm text-muted-foreground border rounded p-4 bg-white'>
          Create an AI player to start configuration.
        </div>
      )}

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create AI Player</DialogTitle>
          </DialogHeader>
          <div className='space-y-3'>
            <div className='space-y-1'>
              <Label>Name</Label>
              <Input value={botName} onChange={(e) => setBotName(e.target.value)} />
            </div>
            <div className='space-y-1'>
              <Label>Persona Instructions</Label>
              <Input
                value={botPersona}
                onChange={(e) => setBotPersona(e.target.value)}
              />
            </div>
            <div className='flex justify-end'>
              <Button
                className='bg-dark-blue hover:bg-dark-blue-hovered'
                onClick={createBot}
                disabled={creating}
              >
                {creating ? 'Creating...' : 'Create'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmationModal
        isOpen={showDelete}
        onClose={() => setShowDelete(false)}
        onConfirm={deleteBot}
        title='Delete AI Player'
        description={
          selectedBot
            ? `Delete "${selectedBot.name}"? This action cannot be undone.`
            : 'Delete selected AI player?'
        }
        confirmButtonText='Delete'
        cancelButtonText='Cancel'
      />

      {roomId && importAIPlayersOpen && (
        <ImportModal
          key={`${roomId}-ai-import`}
          isOpen={importAIPlayersOpen}
          onClose={() => setImportAIPlayersOpen(false)}
          scope='ai_players'
          roomId={roomId}
          onImportComplete={() => {
            setImportAIPlayersOpen(false)
            void loadBots()
          }}
        />
      )}
    </div>
  )
}
